import moment from 'moment';
import { getCalendarDateRange, enumerateCalendarDates } from './scheduler-util'

import templateUrl from './scheduler.component.html';
import './scheduler.component.less'


/**
 * A scheduler that can display a list of schedules over a period
 * of time.
 */
export const SchedulerComponent = {
    templateUrl,
    bindings: {
        schedules: "<", // See schedule.model.ts
        caption: "@", // Caption for the scheduler, displayed in top left corner.
        calendarOptions: "<",
        scheduleActions: "<?", // Defines a list of additional actions. See ScheduleAction interface.
        calendarMode: "<?", // day/week/month
        calendarStart: "<?", // Start date on the scheduler's calendar range.
        editSchedule: "&onEditSchedule",
        deleteSchedule: "&onDeleteSchedule",
        dropIntoSchedule: "&onDropIntoSchedule",
        clickTimeSlot: "&onClickTimeSlot", // Pass schedule and optional time slot data.
        doubleClickTimeSlot: "&onDoubleClickTimeSlot", // Pass schedule and optional time slot data.
        createTimeSlot: "&onCreateTimeSlot", // Signal that the scheduler created a time slot on a schedule.
        deleteTimeSlot: "&onDeleteTimeSlot", // Signal that the scheduler is deleting a time slot.
        editTimeSlot: "&onEditTimeSlot", // Signal that the scheduler is editing a time slot.
        moveTimeSlot: "&onMoveTimeSlot", // Propagate event when resizing or moving a time slot between schedules.
        changeCalendar: "&onChangeCalendar" // Propagate event when changing calendar date or mode
    },
    controller: class SchedulerComponent {
        constructor() {
            'ngInject'
            this.calendar = {
                mode: "week",
                ...getCalendarDateRange("week")
            };
            this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);
            this.defaultCalendarOptions = {
                calendarModes: ["day", "week", "month"],
                enableSettings: false
            };
            this.calendarOptions = this.defaultCalendarOptions;
        }

        $onInit() {
            this.caption = this.caption || "Schedules";
            this.scheduleActions = this.scheduleActions || [];
        }

        $onChanges(changesObj) {
            if (changesObj.calendarMode) {
                this.calendar.mode = this.calendarMode || "week";
                Object.assign(this.calendar, getCalendarDateRange(this.calendar.mode, this.calendarStart));
                this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);
            }
            if (changesObj.calendarStart) {
                Object.assign(this.calendar, getCalendarDateRange(this.calendar.mode, this.calendarStart));
                this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);
            }
            if (changesObj.calendarOptions) {
                // Always reapply updated calendar options from bindings over the defaults.
                this.calendarOptions = Object.assign({}, this.defaultCalendarOptions,
                    this.calendarOptions || {});
            }
        }

        onChangeCalendarMode() {
            const startOf = this.calendar.mode === "week" ? "isoWeek" : this.calendar.mode;
            this.calendarStart = moment(this.calendar.startDate).startOf(startOf).toDate();
            Object.assign(this.calendar, getCalendarDateRange(this.calendar.mode, this.calendarStart));
            this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);

            this.changeCalendar({
                $event: {
                    calendar: this.calendar
                }
            });
        }

        onNavigateCalendar(difference, unit) {
            this.calendarStart = moment(this.calendarStart).add(difference, unit).toDate();
            Object.assign(this.calendar, getCalendarDateRange(this.calendar.mode, this.calendarStart));
            this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);

            this.changeCalendar({
                $event: {
                    calendar: this.calendar
                }
            });
        }

        onChangeDate() {
            this.calendarStart = moment(this.calendarStart).startOf(this.calendar.mode);
            Object.assign(this.calendar, getCalendarDateRange(this.calendar.mode, this.calendarStart));
            this.calendarDates = enumerateCalendarDates(this.calendar.startDate, this.calendar.endDate);

            this.changeCalendar({
                $event: {
                    calendar: this.calendar
                }
            });
        }

        onClickTimeSlot({ schedule, date, timeSlot }) {
            this.clickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onDoubleClickTimeSlot({ schedule, date, timeSlot }) {
            this.doubleClickTimeSlot({
                $event: {
                    schedule,
                    timeSlot: timeSlot || {
                        startDateTime: moment(date).startOf("day").toDate(),
                        endDateTime: moment(date).endOf("day").toDate()
                    }
                }
            });
        }

        onEditSchedule(schedule) {
            return this.editSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDeleteSchedule(schedule) {
            return this.deleteSchedule({
                $event: {
                    schedule: schedule
                }
            });
        }

        onDropIntoSchedule(schedule, payload) {
            return this.dropIntoSchedule({
                $event: {
                    schedule,
                    payload
                }
            })
        }

        onEditTimeSlot(schedule, timeSlot) {
            return this.editTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onDeleteTimeSlot(schedule, timeSlot) {
            return this.deleteTimeSlot({
                $event: {
                    schedule,
                    timeSlot
                }
            })
        }

        onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate }) {
            return this.moveTimeSlot({
                $event: {
                    oldValue: {
                        schedule,
                        timeSlot
                    },
                    newValue: {
                        schedule: newSchedule,
                        timeSlot: {
                            startDateTime: moment(newDate).startOf("day").toDate(),
                            endDateTime: moment(newDate).endOf("day").toDate()
                        }
                    }
                }
            });
        }

        onDropSchedulePayload({ payload: { schedule, timeSlot }, newSchedule, newDate }) {
            // Handle drop operations. If payload has an id, perform a move.
            // Otherwise, create a new time slot from the payload data.
            if (timeSlot.id) {
                return this.onMoveTimeSlot({ schedule, timeSlot, newSchedule, newDate });
            } else {
                return this.createTimeSlot({
                    $event: {
                        schedule: newSchedule,
                        date: newDate,
                        timeSlot
                    }
                });
            }
        }
    }
};
